import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
    FaFileAlt, FaBell, FaCalendarAlt, FaBullhorn, FaPlus,
    FaCheckCircle, FaClock, FaArrowRight, FaTh,
    FaUser, FaChartBar, FaCog, FaUsers, FaBriefcase, FaEnvelope
} from 'react-icons/fa';
import { applicationAPI, announcementAPI, notificationAPI, sessionAPI, jobAPI } from '../../services/api';
import {
    DashboardHeader,
    DashboardFilters,
    DashboardTabs,
    StatCard,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ApplicantDashboard = () => {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [filters, setFilters] = useState({
        status: 'all',
        period: 'this_month'
    });
    const [applications, setApplications] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [activeSessions, setActiveSessions] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [stats, setStats] = useState({
        totalApplications: 0,
        pendingApplications: 0,
        selectedApplications: 0,
        activeSessions: 0
    });
    const [trends, setTrends] = useState({
        totalApplications: 0,
        pendingApplications: 0,
        selectedApplications: 0,
        activeSessions: 0
    });

    // Use refs to prevent duplicate calls and track loading state
    const dashboardDataLoadedRef = useRef(false);
    const isLoadingDashboardRef = useRef(false);

    // Load dashboard data ONCE on mount - no dependencies to prevent loops
    useEffect(() => {
        // CRITICAL: Check refs at the very start - prevent ANY re-execution
        if (dashboardDataLoadedRef.current) {
            return;
        }
        
        // Mark as loading immediately to prevent any concurrent calls
        if (isLoadingDashboardRef.current) {
            return;
        }

        // Set both flags IMMEDIATELY before any async operations
        isLoadingDashboardRef.current = true;
        dashboardDataLoadedRef.current = true;

        const loadData = async () => {
            try {
                const [appsRes, annRes, notifRes, sessionsRes] = await Promise.all([
                    applicationAPI.getMyApplications(),
                    announcementAPI.getMyFeed(),
                    notificationAPI.getUnreadCount(),
                    sessionAPI.getActive()
                ]);

                // Double-check refs after API call to prevent state updates if component unmounted
                if (!dashboardDataLoadedRef.current) {
                    return; // Component was reset or unmounted
                }

                const apps = appsRes.applications || [];
                const pending = apps.filter(a => ['draft', 'submitted', 'under_review'].includes(a.status)).length;
                const selected = apps.filter(a => a.status === 'selected').length;
                const sessions = sessionsRes.sessions || [];
                const sessionsCount = sessions.length;

                const calculateTrend = (current) => {
                    const previous = current * 0.9;
                    if (current === 0) return 0;
                    return ((current - previous) / previous) * 100;
                };

                // Batch all state updates together using React.startTransition or just call them sequentially
                // React 18 automatically batches these, but we'll be explicit
                // CRITICAL: Only update state if data actually changed to prevent infinite loops
                setApplications(prev => {
                    const prevStr = JSON.stringify(prev);
                    const newStr = JSON.stringify(apps);
                    return prevStr === newStr ? prev : apps;
                });
                setAnnouncements(prev => {
                    const newAnn = annRes.announcements?.slice(0, 5) || [];
                    const prevStr = JSON.stringify(prev);
                    const newStr = JSON.stringify(newAnn);
                    return prevStr === newStr ? prev : newAnn;
                });
                setUnreadCount(prev => {
                    const newCount = notifRes.count || 0;
                    return prev === newCount ? prev : newCount;
                });
                setActiveSessions(prev => {
                    const prevStr = JSON.stringify(prev);
                    const newStr = JSON.stringify(sessions);
                    return prevStr === newStr ? prev : sessions;
                });
                setStats({
                    totalApplications: apps.length,
                    pendingApplications: pending,
                    selectedApplications: selected,
                    activeSessions: sessionsCount
                });
                setTrends({
                    totalApplications: calculateTrend(apps.length),
                    pendingApplications: calculateTrend(pending),
                    selectedApplications: calculateTrend(selected),
                    activeSessions: calculateTrend(sessionsCount)
                });
            } catch (err) {
                console.error('Failed to load dashboard:', err);
            } finally {
                // Only update loading state if still mounted
                if (dashboardDataLoadedRef.current) {
                    setLoading(false);
                }
                isLoadingDashboardRef.current = false;
            }
        };

        loadData();
        
        // Cleanup function to reset if component unmounts
        return () => {
            // Don't reset dashboardDataLoadedRef - we want it to stay true
            isLoadingDashboardRef.current = false;
        };
    }, []); // Empty deps - only run once on mount

    // Check for application submission success
    // Extract the specific param value to avoid object reference changes
    const applicationSubmitted = searchParams.get('application_submitted');
    useEffect(() => {
        if (applicationSubmitted === 'true') {
            toast.success('Application submitted successfully! You will receive a confirmation email shortly.', {
                duration: 5000,
                icon: '✅'
            });
            // Remove the query parameter from URL
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [applicationSubmitted]); // Use primitive value instead of object

    // Load tab data when tab changes - separate from dashboard load
    // Use ref to prevent duplicate calls when activeTab changes rapidly
    const tabLoadingRef = useRef(false);
    const lastActiveTabRef = useRef(activeTab);
    
    useEffect(() => {
        // Skip if already loading or tab hasn't actually changed
        if (tabLoadingRef.current || lastActiveTabRef.current === activeTab) {
            return;
        }
        
        if (activeTab === 'dashboard') {
            lastActiveTabRef.current = activeTab;
            return; // Dashboard tab uses data already loaded
        }

        tabLoadingRef.current = true;
        lastActiveTabRef.current = activeTab;
        setTabLoading(true);

        const loadTabData = async () => {
            try {
                if (activeTab === 'applications') {
                    const res = await applicationAPI.getMyApplications();
                    setApplications(res.applications || []);
                } else if (activeTab === 'announcements') {
                    const res = await announcementAPI.getMyFeed();
                    setAnnouncements(res.announcements || []);
                } else if (activeTab === 'sessions') {
                    const res = await sessionAPI.getActive();
                    const newSessions = res.sessions || [];
                    setActiveSessions(prev => {
                        const prevStr = JSON.stringify(prev);
                        const newStr = JSON.stringify(newSessions);
                        return prevStr === newStr ? prev : newSessions;
                    });
                } else if (activeTab === 'jobs') {
                    const res = await jobAPI.getAll();
                    setJobs(res.jobs || []);
                } else if (activeTab === 'messages') {
                    // TODO: Implement messages API
                    setMessages([]);
                }
            } catch (err) {
                console.error('Failed to load tab data:', err);
            } finally {
                setTabLoading(false);
                tabLoadingRef.current = false;
            }
        };

        loadTabData();
    }, [activeTab]); // Only depend on activeTab

    const filterConfig = [
        {
            key: 'status',
            options: [
                { value: 'all', label: 'All Status' },
                { value: 'draft', label: 'Draft' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'under_review', label: 'Under Review' },
                { value: 'selected', label: 'Selected' }
            ]
        },
        {
            key: 'period',
            options: [
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'this_year', label: 'This Year' },
                { value: 'all_time', label: 'All Time' }
            ]
        }
    ];

    const tabs = [
        { id: 'dashboard', label: 'Dashboard Overview', icon: FaTh },
        { id: 'applications', label: 'My Applications', icon: FaFileAlt },
        { id: 'announcements', label: 'Announcements', icon: FaBullhorn },
        { id: 'sessions', label: 'Active Sessions', icon: FaCalendarAlt },
        { id: 'community', label: 'Community Forum', icon: FaUsers },
        { id: 'jobs', label: 'Job Board', icon: FaBriefcase },
        { id: 'messages', label: 'Messages', icon: FaEnvelope },
        { id: 'reports', label: 'Reports', icon: FaChartBar },
        { id: 'settings', label: 'Settings', icon: FaCog }
    ];

    if (loading) {
        return <LoadingSpinner />;
    }

    const hasActiveApplication = applications.some(app => 
        ['draft', 'submitted', 'under_review', 'interview_scheduled'].includes(app.status)
    );

    const filteredApplications = filters.status === 'all' 
        ? applications 
        : applications.filter(app => app.status === filters.status);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <DashboardHeader
                    icon={FaFileAlt}
                    title="Applicant Dashboard"
                    subtitle="Track your scholarship applications here"
                />

                <DashboardFilters
                    filters={filters}
                    onFilterChange={setFilters}
                    onClearFilters={() => setFilters({ status: 'all', period: 'this_month' })}
                    filterConfig={filterConfig}
                />

                <DashboardTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {activeTab === 'dashboard' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            <StatCard
                                title="My Applications"
                                value={stats.totalApplications}
                                trend={trends.totalApplications}
                                icon={FaFileAlt}
                                iconColor="bg-blue-50 text-blue-600"
                            />
                            <StatCard
                                title="Pending Applications"
                                value={stats.pendingApplications}
                                trend={trends.pendingApplications}
                                icon={FaClock}
                                iconColor="bg-yellow-50 text-yellow-600"
                            />
                            <StatCard
                                title="Selected Applications"
                                value={stats.selectedApplications}
                                trend={trends.selectedApplications}
                                icon={FaCheckCircle}
                                iconColor="bg-green-50 text-green-600"
                            />
                            <StatCard
                                title="Unread Notifications"
                                value={unreadCount}
                                trend={0}
                                icon={FaBell}
                                iconColor="bg-purple-50 text-purple-600"
                            />
                            <StatCard
                                title="Active Sessions"
                                value={stats.activeSessions}
                                trend={trends.activeSessions}
                                icon={FaCalendarAlt}
                                iconColor="bg-indigo-50 text-indigo-600"
                            />
                            <StatCard
                                title="Announcements"
                                value={announcements.length}
                                trend={0}
                                icon={FaBullhorn}
                                iconColor="bg-pink-50 text-pink-600"
                            />
                        </div>

                        {!hasActiveApplication && activeSessions.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-blue-900 mb-1">New scholarship sessions are open!</h3>
                                        <p className="text-blue-700">Start your application today</p>
                                    </div>
                                    <Link
                                        to="/apply"
                                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md"
                                    >
                                        <FaPlus /> Start Application
                                    </Link>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">My Applications</h2>
                                {filteredApplications.length === 0 ? (
                                    <EmptyState
                                        icon={FaFileAlt}
                                        message="No applications yet"
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {filteredApplications.slice(0, 5).map((app) => (
                                            <Link
                                                key={app.id}
                                                to={`/applications/${app.id}`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">{app.session_name}</span>
                                                        <span className="text-gray-600 text-sm ml-2 block">
                                                            Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Recently'}
                                                        </span>
                                                    </div>
                                                    <StatusBadge status={app.status} />
                                                </div>
                                            </Link>
                                        ))}
                                        {filteredApplications.length > 5 && (
                                            <Link
                                                to="/applications"
                                                className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                            >
                                                View all applications <FaArrowRight className="inline ml-1" />
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Latest Announcements</h2>
                                {announcements.length === 0 ? (
                                    <EmptyState
                                        icon={FaBullhorn}
                                        message="No announcements available"
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {announcements.map((ann) => (
                                            <Link
                                                key={ann.id}
                                                to={`/announcements/${ann.id}`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">{ann.title}</span>
                                                        <span className="text-gray-600 text-sm ml-2 block line-clamp-2">
                                                            {ann.content?.substring(0, 100)}...
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                        <Link
                                            to="/announcements"
                                            className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                        >
                                            View all announcements <FaArrowRight className="inline ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'applications' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">My Applications</h2>
                            <Link
                                to="/apply"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                                <FaPlus /> New Application
                            </Link>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading applications...</p>
                            </div>
                        ) : filteredApplications.length === 0 ? (
                            <div className="text-center py-8">
                                <EmptyState
                                    icon={FaFileAlt}
                                    message="No applications found"
                                />
                                <Link
                                    to="/apply"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium mt-4"
                                >
                                    <FaPlus /> Create Application
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredApplications.map((app) => (
                                    <Link
                                        key={app.id}
                                        to={`/applications/${app.id}`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{app.session_name || 'Scholarship Application'}</span>
                                                <span className="text-gray-600 text-sm ml-2 block">
                                                    Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Recently'}
                                                </span>
                                            </div>
                                            <StatusBadge status={app.status} />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'announcements' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">All Announcements</h2>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading announcements...</p>
                            </div>
                        ) : announcements.length === 0 ? (
                            <EmptyState
                                icon={FaBullhorn}
                                message="No announcements available"
                            />
                        ) : (
                            <div className="space-y-3">
                                {announcements.map((ann) => (
                                    <Link
                                        key={ann.id}
                                        to={`/announcements/${ann.id}`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{ann.title}</span>
                                                <span className="text-gray-600 text-sm ml-2 block line-clamp-2">
                                                    {ann.content?.substring(0, 150)}...
                                                </span>
                                                <span className="text-gray-500 text-xs ml-2 block mt-1">
                                                    {ann.created_at ? new Date(ann.created_at).toLocaleDateString() : 'Recently'}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                                <Link
                                    to="/announcements"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Announcements <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'sessions' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Active Sessions</h2>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading sessions...</p>
                            </div>
                        ) : activeSessions.length === 0 ? (
                            <EmptyState
                                icon={FaCalendarAlt}
                                message="No active sessions available"
                            />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {activeSessions.map((session) => (
                                    <div
                                        key={session.id}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <h3 className="font-semibold text-gray-900 mb-2">{session.name}</h3>
                                        <p className="text-gray-600 text-sm mb-2">{session.description}</p>
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <FaClock className="text-xs" />
                                            <span>
                                                {session.start_date && new Date(session.start_date).toLocaleDateString()} - 
                                                {session.end_date && new Date(session.end_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <Link
                                            to="/apply"
                                            className="inline-block mt-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
                                        >
                                            Apply Now <FaArrowRight className="inline ml-1" />
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'community' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Community Forum</h2>
                            <Link
                                to="/community"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                                <FaPlus /> Create Post
                            </Link>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Connect with other applicants, alumni, and student managers. Share experiences, ask questions, and stay updated.
                        </p>
                        <Link
                            to="/community"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all font-medium"
                        >
                            <FaUsers /> Visit Community Forum <FaArrowRight />
                        </Link>
                    </div>
                )}

                {activeTab === 'jobs' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Job Board (View Only)</h2>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                            <p className="text-yellow-800 text-sm">
                                <strong>Note:</strong> You can view job opportunities here. To apply for jobs, you must first be selected as a student.
                            </p>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading jobs...</p>
                            </div>
                        ) : jobs.length === 0 ? (
                            <EmptyState
                                icon={FaBriefcase}
                                message="No job opportunities available"
                            />
                        ) : (
                            <div className="space-y-4">
                                {jobs.slice(0, 10).map((job) => (
                                    <div
                                        key={job.id}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900 mb-1">{job.title}</h3>
                                                <p className="text-gray-600 text-sm mb-2 line-clamp-2">{job.description}</p>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>{job.location}</span>
                                                    <span>{job.job_type}</span>
                                                    {job.salary_range && <span>{job.salary_range}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <Link
                                    to="/jobs"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Jobs <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'messages' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Messages</h2>
                        <p className="text-gray-600 mb-4">
                            Messages from Student Managers and Administrators regarding your applications.
                        </p>
                        {messages.length === 0 ? (
                            <EmptyState
                                icon={FaEnvelope}
                                message="No messages yet"
                            />
                        ) : (
                            <div className="space-y-3">
                                {messages.map((message) => (
                                    <div
                                        key={message.id}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-1">{message.subject}</h3>
                                                <p className="text-gray-600 text-sm mb-2">{message.content}</p>
                                                <span className="text-gray-500 text-xs">
                                                    From: {message.from_name} • {message.created_at ? new Date(message.created_at).toLocaleDateString() : 'Recently'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Application Reports</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Total Applications</h3>
                                <p className="text-3xl font-bold text-blue-600 mb-1">{stats.totalApplications}</p>
                                <p className="text-sm text-gray-600">All time</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Success Rate</h3>
                                <p className="text-3xl font-bold text-green-600 mb-1">
                                    {stats.totalApplications > 0 
                                        ? ((stats.selectedApplications / stats.totalApplications) * 100).toFixed(1)
                                        : 0}%
                                </p>
                                <p className="text-sm text-gray-600">Selected vs Total</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Pending</h3>
                                <p className="text-3xl font-bold text-yellow-600 mb-1">{stats.pendingApplications}</p>
                                <p className="text-sm text-gray-600">Under Review</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>
                        <div className="space-y-4">
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Profile Settings</h3>
                                <p className="text-gray-600 text-sm mb-3">Update your personal information and preferences</p>
                                <Link
                                    to="/profile/edit"
                                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                                >
                                    Edit Profile →
                                </Link>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Notification Preferences</h3>
                                <p className="text-gray-600 text-sm">Manage how you receive notifications</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicantDashboard;
